import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, RotateCcw, Check } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { adminApi, AdminDispute } from '../../api';

const AdminDisputes: React.FC = () => {
  const [disputes, setDisputes] = useState<AdminDispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    adminApi.listDisputes()
      .then(setDisputes)
      .catch(() => setDisputes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const reopen = async (d: AdminDispute) => {
    if (!window.confirm(`Reopen "${d.name}" so players can fix and re-confirm the scores?`)) return;
    setBusyId(d.id); setError('');
    try { await adminApi.reopenGame(d.id); load(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Could not reopen'); setBusyId(null); }
  };

  const approve = async (d: AdminDispute) => {
    if (!window.confirm(`Accept the submitted scores for "${d.name}" as final and clear the dispute? Bets can then be settled.`)) return;
    setBusyId(d.id); setError('');
    try { await adminApi.approveGameResults(d.id); load(); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Could not approve'); setBusyId(null); }
  };

  return (
    <AdminLayout>
      {error && <div className="bg-red-500/15 text-red-200 border border-red-500/30 p-3 rounded-xl text-sm mb-4">{error}</div>}
      {loading ? (
        <div className="text-green-200/80 py-10 text-center">Loading…</div>
      ) : disputes.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-500">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          No disputed games. All settled. ⛳
        </div>
      ) : (
        <div className="space-y-3">
          {disputes.map((d) => (
            <div key={d.id} className="bg-white rounded-2xl p-4 shadow-sm border border-red-200">
              <button onClick={() => navigate(`/game/${d.id}`)} className="w-full flex items-center justify-between text-left">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{d.name}</p>
                  <p className="text-xs text-gray-500 truncate">{d.course_name} · {d.player_count} players</p>
                  <p className="text-xs text-gray-400">{new Date(d.scheduled_date).toLocaleDateString()}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
              </button>

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => reopen(d)}
                  disabled={busyId === d.id}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-amber-500 text-white py-2.5 rounded-xl font-semibold hover:bg-amber-600 transition disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4" /> Reopen to fix
                </button>
                <button
                  onClick={() => approve(d)}
                  disabled={busyId === d.id}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 text-white py-2.5 rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
                >
                  <Check className="h-4 w-4" /> Accept scores
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Reopen lets players correct scores; Accept keeps the scores as-is and clears the dispute so bets can settle.
              </p>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDisputes;
